import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation, Link, useNavigate } from 'react-router-dom';
import { useToast } from './hooks/useToast';
import { cariAPI, productAPI, cashAPI, incomeAPI } from './services/api';
import { ModuleKey, normalizeAllowedModules } from './utils/moduleAccess';

// Lazy load pages for better performance
const LoginPage = lazy(() => import('./pages/LoginPage'));
const CurrentAccountsPage = lazy(() => import('./pages/CurrentAccountsPage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const CashRegistersPage = lazy(() => import('./pages/CashRegistersPage'));
const IncomeExpensesPage = lazy(() => import('./pages/IncomeExpensesPage'));
const SalesPage = lazy(() => import('./pages/SalesPage'));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const CompaniesPage = lazy(() => import('./pages/CompaniesPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const UnifiedDashboardPage = lazy(() => import('./pages/UnifiedDashboardPage'));
const AdminProfileModal = lazy(() => import('./pages/AdminProfileModal'));
const MobileInstallPrompt = lazy(() => import('./components/MobileInstallPrompt'));

// Loading component
const PageLoader: React.FC = () => (
  <div style={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    minHeight: '40vh',
    flexDirection: 'column',
    gap: 16
  }}>
    <div style={{ 
      width: 40,
      height: 40,
      border: '3px solid #e5e7eb',
      borderTopColor: '#2563eb',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite'
    }}></div>
    <div style={{ 
      fontSize: 14, 
      color: '#6b7280',
    }}>
      Yükleniyor...
    </div>
    <style>{`
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

const companyMenu = [
  { path: '/cari-hesaplar', label: 'Cari Hesaplar' },
  { path: '/urunler', label: 'Stok Yönetimi' },
  { path: '/kasalar', label: 'Kasa Yönetimi' },
  { path: '/gelir-gider', label: 'Gelir - Gider' },
];

const adminMenu: any[] = []; // Menüleri kaldırdık, tek sayfada yönetiyoruz

const AdminLayout: React.FC<{ children: React.ReactNode; title: string; handleLogout: () => void }> = ({ 
  children, 
  title, 
  handleLogout 
}) => {
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const adminData = localStorage.getItem('currentAdmin');
  const admin = adminData ? JSON.parse(adminData) : null;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f8fafc'
    }}>
      {/* Minimal Header - Responsive */}
      <header style={{
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        padding: isMobile ? '10px 12px' : '12px clamp(12px, 2vw, 24px)',
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
      }}>
        {isMobile ? (
          // Mobil görünüm: Dikey düzen
          <>
            {/* Üst satır: Logo ve butonlar */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 8,
            }}>
              <img 
                src={(() => {
                  if (typeof window !== 'undefined') {
                    return localStorage.getItem('siteLogo') || '/kayasoftlogo.png';
                  }
                  return '/kayasoftlogo.png';
                })()}
                alt="Kayasoft Logo" 
                style={{
                  height: '28px',
                  width: 'auto',
                  maxWidth: '120px',
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/kayasofticon.png';
                }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button
                  onClick={() => setShowProfileModal(true)}
                  style={{
                    padding: '6px 10px',
                    background: '#fff',
                    color: '#6b7280',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: 12,
                  }}
                >
                  ⚙️
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    padding: '6px 10px',
                    background: '#fff',
                    color: '#dc2626',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 500,
                    fontSize: 12,
                  }}
                >
                  Çıkış
                </button>
              </div>
            </div>
            {/* Alt satır: Başlık ve kullanıcı adı */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flex: 1,
                overflow: 'hidden',
              }}>
                <img 
                  src="/wepra.png" 
                  alt="Wepra Logo" 
                  style={{
                    height: 36,
                    width: 'auto',
                    maxWidth: 180,
                    objectFit: 'contain',
                    flexShrink: 0,
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
              {admin && (
                <div style={{ 
                  padding: '4px 8px', 
                  background: '#f3f4f6', 
                  borderRadius: 4,
                  fontSize: 11,
                  color: '#6b7280',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '150px',
                }}>
                  {admin.fullName || admin.email}
                </div>
              )}
            </div>
          </>
        ) : (
          // Desktop görünüm: Grid düzen
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            gap: 'clamp(8px, 2vw, 16px)',
          }}>
            {/* Sol: Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, justifyContent: 'flex-start' }}>
              <img 
                src={(() => {
                  if (typeof window !== 'undefined') {
                    return localStorage.getItem('siteLogo') || '/kayasoftlogo.png';
                  }
                  return '/kayasoftlogo.png';
                })()}
                alt="Kayasoft Logo" 
                style={{
                  height: 'clamp(32px, 4vw, 40px)',
                  width: 'auto',
                  maxWidth: 'clamp(150px, 20vw, 200px)',
                  objectFit: 'contain',
                  flexShrink: 0,
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/kayasofticon.png';
                }}
              />
            </div>
            {/* Orta: Başlık */}
            <div style={{ 
              display: 'flex',
              justifyContent: 'center',
              fontSize: 'clamp(14px, 2vw, 18px)',
              fontWeight: 600,
              color: '#1f2937',
              whiteSpace: 'nowrap',
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}>
              {(() => {
                if (typeof window !== 'undefined') {
                  return localStorage.getItem('siteTitle') || 'Cari-Stok Uygulaması';
                }
                return 'Cari-Stok Uygulaması';
              })()}
            </div>
            {/* Sağ: Kullanıcı bilgileri ve butonlar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {admin && (
                <div style={{ 
                  padding: '6px 12px', 
                  background: '#f3f4f6', 
                  borderRadius: 6,
                  fontSize: 13,
                  color: '#6b7280',
                  fontWeight: 500
                }}>
                  {admin.fullName || admin.email}
                </div>
              )}
              <button
                onClick={() => setShowProfileModal(true)}
                style={{
                  padding: '8px 12px',
                  background: '#fff',
                  color: '#6b7280',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: 13,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f9fafb';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                Ayarlar
              </button>
              <button
                onClick={handleLogout}
                style={{
                  padding: '8px 12px',
                  background: '#fff',
                  color: '#dc2626',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 500,
                  fontSize: 13,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fef2f2';
                  e.currentTarget.style.borderColor = '#fecaca';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = '#e5e7eb';
                }}
              >
                Çıkış
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main style={{ 
        padding: '24px',
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%'
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 8,
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}>
          {React.cloneElement(children as React.ReactElement, { 
            showProfileModal, 
            setShowProfileModal,
            onProfileUpdate: () => {
              const updatedAdmin = localStorage.getItem('currentAdmin');
              if (updatedAdmin) {
                window.location.reload();
              }
            }
          })}
        </div>
      </main>
      <Suspense fallback={null}>
        <AdminProfileModal
          isOpen={showProfileModal}
          onClose={() => setShowProfileModal(false)}
          onUpdate={() => {
            const updatedAdmin = localStorage.getItem('currentAdmin');
            if (updatedAdmin) {
              window.location.reload();
            }
          }}
        />
      </Suspense>
    </div>
  );
};

const CompanyLayout: React.FC<{ children: React.ReactNode; menu: typeof companyMenu; handleLogout: () => void }> = ({ 
  children, 
  menu,
  handleLogout 
}) => {
  const location = useLocation();
  const companyData = localStorage.getItem('currentCompany');
  const company = companyData ? JSON.parse(companyData) : null;
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    cariCount: 0,
    productCount: 0,
    cashCount: 0,
    incomeTotal: 0,
    expenseTotal: 0,
  });
  const [now, setNow] = useState<Date>(new Date());
  const [usdTry, setUsdTry] = useState<number | null>(null);
  const [rateLoading, setRateLoading] = useState<boolean>(false);
  const [rateError, setRateError] = useState<string>('');

  const fetchUsdRate = async () => {
    setRateError('');
    try {
      setRateLoading(true);
      
      // Birden fazla API kaynağını dene
      const apis = [
        // 1. exchangerate-api.com (ücretsiz, güvenilir)
        async () => {
          const res = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
          if (!res.ok) throw new Error('API 1 failed');
          const data = await res.json();
          return data?.rates?.TRY;
        },
        // 2. exchangerate.host (eski API)
        async () => {
          const res = await fetch('https://api.exchangerate.host/latest?base=USD&symbols=TRY');
          if (!res.ok) throw new Error('API 2 failed');
          const data = await res.json();
          return data?.rates?.TRY;
        },
        // 3. fawazahmed0 currency API (GitHub)
        async () => {
          const res = await fetch('https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json');
          if (!res.ok) throw new Error('API 3 failed');
          const data = await res.json();
          return data?.usd?.try;
        }
      ];

      let rate = null;
      let lastError = null;

      // Her API'yi sırayla dene, ilk başarılı olanı kullan
      for (const apiFunc of apis) {
        try {
          rate = await apiFunc();
          if (rate && !isNaN(Number(rate)) && Number(rate) > 0) {
            setUsdTry(Number(rate));
            setRateError('');
            setRateLoading(false);
            return; // Başarılı, çık
          }
        } catch (err: any) {
          lastError = err;
          console.warn('API denemesi başarısız:', err.message);
          continue; // Bir sonraki API'yi dene
        }
      }

      // Tüm API'ler başarısız oldu
      if (!rate) {
        setRateError('Kur bilgisi alınamadı');
        console.error('Tüm API kaynakları başarısız oldu:', lastError);
      }
    } catch (err: any) {
      setRateError('Kur servisine ulaşılamadı');
      console.error('Kur bilgisi hatası:', err);
    } finally {
      setRateLoading(false);
    }
  };

  useEffect(() => {
    const loadStats = async () => {
      try {
        const [cari, products, cashes, incomes] = await Promise.all([
          cariAPI.list(),
          productAPI.list(),
          cashAPI.list(),
          incomeAPI.list(),
        ]);

        const incomeItems = incomes.data || [];

        setStats({
          cariCount: (cari.data || []).length,
          productCount: (products.data || []).length,
          cashCount: (cashes.data || []).length,
          incomeTotal: incomeItems
            .filter((item: any) => item.type === 'Gelir')
            .reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0),
          expenseTotal: incomeItems
            .filter((item: any) => item.type === 'Gider')
            .reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0),
        });
      } catch (error) {
        console.error('Stats yüklenirken hata:', error);
      }
    };

    loadStats();
    
    // Stats'ı güncellemek için event listener ekle
    const handleStatsUpdate = () => {
      loadStats();
    };
    
    window.addEventListener('statsUpdate', handleStatsUpdate);
    
    // Location değiştiğinde stats'ı yeniden yükle (özellikle gelir/gider ve kasa/banka sayfalarından dönüldüğünde)
    const pathname = location.pathname;
    if (pathname === '/') {
      loadStats();
    }
    
    return () => {
      window.removeEventListener('statsUpdate', handleStatsUpdate);
    };
  }, [location]);

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    fetchUsdRate();
    const rateTimer = setInterval(fetchUsdRate, 30 * 60 * 1000); // 30 dakikada bir güncelle
    return () => {
      clearInterval(timer);
      clearInterval(rateTimer);
    };
  }, []);

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#f8fafc'
    }}>
      {/* Minimal Header */}
      <header style={{
        background: 'rgba(255, 255, 255, 0.95)',
        borderBottom: '1px solid #e5e7eb',
        padding: '16px 24px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        backdropFilter: 'blur(10px)',
      }}>
        {/* Top Row: Logo, Title, User Info */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          position: 'relative',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: '0 0 auto' }}>
            <img 
              src="/kayasofticon.png" 
              alt="Kayasoft Logo" 
              style={{
                height: 40,
                width: 'auto',
                objectFit: 'contain',
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <div style={{ 
            position: 'absolute', 
            left: '50%', 
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <img 
              src="/wepra.png" 
              alt="Wepra Logo" 
              style={{
                height: 48,
                width: 'auto',
                maxWidth: 270,
                objectFit: 'contain',
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto' }}>
              <div style={{ 
                padding: '8px 12px',
                background: '#f1f5f9',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                minWidth: 160
              }}>
                <div style={{ fontSize: 12, color: '#475569', fontWeight: 600 }}>
                  {now.toLocaleDateString('tr-TR', { weekday: 'long', day: '2-digit', month: 'long' })}
                </div>
                <div style={{ fontSize: 12, color: '#475569' }}>
                  {now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                </div>
                <div style={{ fontSize: 12, color: '#0f172a', marginTop: 4, fontWeight: 600 }}>
                  USD/TRY: {rateLoading ? 'yükleniyor...' : usdTry ? usdTry.toFixed(2) : '—'}
                </div>
                {rateError && (
                  <div style={{ fontSize: 11, color: '#b91c1c' }}>{rateError}</div>
                )}
              </div>
              {company && (() => {
                const companyLogo = localStorage.getItem('companyLogo');
                if (companyLogo) {
                  return (
                    <img 
                      src={companyLogo} 
                      alt="Company Logo" 
                      style={{ 
                        maxHeight: '40px', 
                        maxWidth: '200px', 
                        objectFit: 'contain',
                        borderRadius: 4,
                      }}
                      onError={(e) => {
                        // Logo yüklenemezse firma adını göster
                        const div = document.createElement('div');
                        div.style.padding = '6px 12px';
                        div.style.background = '#f3f4f6';
                        div.style.borderRadius = '6px';
                        div.style.fontSize = '13px';
                        div.style.color = '#6b7280';
                        div.style.fontWeight = '500';
                        div.textContent = company.companyName || company.email;
                        e.currentTarget.parentNode?.replaceChild(div, e.currentTarget);
                      }}
                    />
                  );
                }
                return (
                  <div style={{ 
                    padding: '6px 12px', 
                    background: '#f3f4f6', 
                    borderRadius: 6,
                    fontSize: 13,
                    color: '#6b7280',
                    fontWeight: 500
                  }}>
                    {company.companyName || company.email}
                  </div>
                );
              })()}
            <button
              onClick={() => navigate('/profil')}
              style={{
                padding: '8px 12px',
                background: '#fff',
                color: '#6b7280',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: 13,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.borderColor = '#d1d5db';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              Ayarlar
            </button>
            <button
              onClick={handleLogout}
              style={{
                padding: '8px 12px',
                background: '#fff',
                color: '#dc2626',
                border: '1px solid #e5e7eb',
                borderRadius: 6,
                cursor: 'pointer',
                fontWeight: 500,
                fontSize: 13,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#fef2f2';
                e.currentTarget.style.borderColor = '#fecaca';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.borderColor = '#e5e7eb';
              }}
            >
              Çıkış
            </button>
          </div>
        </div>

        {/* Stats Cards - Header altında, sekmeler üstünde */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 20,
          marginBottom: 20,
          maxWidth: '1200px',
          margin: '0 auto 20px auto',
        }}>
          <div className="card-hover" style={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
            borderRadius: 12, 
            padding: 20, 
            border: 'none',
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)',
            color: '#fff'
          }}>
            <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.9)', fontSize: 13, marginBottom: 8, fontWeight: 500 }}>Cari Hesap</p>
            <strong style={{ fontSize: 28, color: '#fff', fontWeight: 700 }}>{stats.cariCount.toLocaleString()}</strong>
          </div>
          <div className="card-hover" style={{ 
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
            borderRadius: 12, 
            padding: 20, 
            border: 'none',
            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)',
            color: '#fff'
          }}>
            <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.9)', fontSize: 13, marginBottom: 8, fontWeight: 500 }}>Ürün</p>
            <strong style={{ fontSize: 28, color: '#fff', fontWeight: 700 }}>{stats.productCount.toLocaleString()}</strong>
          </div>
          <div className="card-hover" style={{ 
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
            borderRadius: 12, 
            padding: 20, 
            border: 'none',
            boxShadow: '0 2px 8px rgba(245, 158, 11, 0.2)',
            color: '#fff'
          }}>
            <p style={{ margin: 0, color: 'rgba(255, 255, 255, 0.9)', fontSize: 13, marginBottom: 8, fontWeight: 500 }}>Kasa/Banka</p>
            <strong style={{ fontSize: 28, color: '#fff', fontWeight: 700 }}>{stats.cashCount.toLocaleString()}</strong>
          </div>
        </div>

        {/* Navigation Tabs */}
        <nav style={{
          display: 'flex',
          gap: 8,
          borderTop: '1px solid #e5e7eb',
          paddingTop: 12,
          background: 'linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))',
        }}>
          {menu.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className="tab-transition"
              style={{
                padding: '12px 20px',
                fontSize: 14,
                fontWeight: 600,
                color: location.pathname === item.path ? '#2563eb' : '#6b7280',
                textDecoration: 'none',
                borderBottom: location.pathname === item.path ? '3px solid #2563eb' : '3px solid transparent',
                borderRadius: '8px 8px 0 0',
                background: location.pathname === item.path ? 'rgba(37, 99, 235, 0.05)' : 'transparent',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                marginBottom: -1,
                position: 'relative',
              }}
              onMouseEnter={(e) => {
                if (location.pathname !== item.path) {
                  e.currentTarget.style.color = '#2563eb';
                  e.currentTarget.style.background = 'rgba(37, 99, 235, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== item.path) {
                  e.currentTarget.style.color = '#6b7280';
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      </header>

      {/* Main Content */}
      <main style={{ 
        padding: '24px',
        maxWidth: '1400px',
        margin: '0 auto',
        width: '100%',
        animation: 'fadeIn 0.3s ease-out',
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: '32px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          minHeight: 'calc(100vh - 300px)',
        }}>
          {children}
        </div>
      </main>
    </div>
  );
};

const Layout: React.FC<{ children: React.ReactNode; isAdmin?: boolean }> = ({ children, isAdmin = false }) => {
  const token = localStorage.getItem('authToken');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  const menu = isAdmin ? adminMenu : companyMenu;
  const title = isAdmin ? '' : 'Cari & Stok Paneli';

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('currentCompany');
    localStorage.removeItem('currentAdmin');
    localStorage.removeItem('userType');
    window.location.href = '/login';
  };

  // Admin için AdminLayout
  if (isAdmin) {
    return <AdminLayout title={title} handleLogout={handleLogout}>{children}</AdminLayout>;
  }

  // Company için CompanyLayout (sidebar yerine header'da tab menü)
  return <CompanyLayout menu={menu} handleLogout={handleLogout}>{children}</CompanyLayout>;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode; requireAdmin?: boolean; requiredModule?: ModuleKey }> = ({ 
  children, 
  requireAdmin = false,
  requiredModule,
}) => {
  const token = localStorage.getItem('authToken');
  const userType = localStorage.getItem('userType');

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && userType !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (!requireAdmin && userType === 'admin') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (!requireAdmin && userType === 'company' && requiredModule) {
    const currentCompanyRaw = localStorage.getItem('currentCompany');
    const currentCompany = currentCompanyRaw ? JSON.parse(currentCompanyRaw) : null;
    const allowedModules = normalizeAllowedModules(
      currentCompany?.allowedModules,
      currentCompany?.moduleType
    );
    if (!allowedModules.includes(requiredModule)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const token = localStorage.getItem('authToken');
  const userType = localStorage.getItem('userType');
  const { ToastContainer } = useToast();

  return (
    <>
      <ToastContainer />
      <Suspense fallback={null}>
        <MobileInstallPrompt />
      </Suspense>
      <Routes>
        <Route 
          path="/login" 
          element={
            <Suspense fallback={<PageLoader />}>
              <LoginPage />
            </Suspense>
          } 
        />
      
      {/* Company Routes - Unified Dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute requireAdmin={false} requiredModule="current_accounts">
            <Suspense fallback={<PageLoader />}>
              <UnifiedDashboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cari-hesaplar"
        element={
          <ProtectedRoute requireAdmin={false} requiredModule="products">
            <Suspense fallback={<PageLoader />}>
              <UnifiedDashboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/urunler"
        element={
          <ProtectedRoute requireAdmin={false} requiredModule="cash">
            <Suspense fallback={<PageLoader />}>
              <UnifiedDashboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/kasalar"
        element={
          <ProtectedRoute requireAdmin={false} requiredModule="income_expense">
            <Suspense fallback={<PageLoader />}>
              <UnifiedDashboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/gelir-gider"
        element={
          <ProtectedRoute requireAdmin={false} requiredModule="checks">
            <Suspense fallback={<PageLoader />}>
              <UnifiedDashboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/cekler"
        element={
          <ProtectedRoute requireAdmin={false} requiredModule="profile">
            <Suspense fallback={<PageLoader />}>
              <UnifiedDashboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profil"
        element={
          <ProtectedRoute requireAdmin={false}>
            <Suspense fallback={<PageLoader />}>
              <UnifiedDashboardPage />
            </Suspense>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute requireAdmin={true}>
            <Layout isAdmin={true}>
              <Suspense fallback={<PageLoader />}>
                <CompaniesPage />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/firmalar"
        element={
          <ProtectedRoute requireAdmin={true}>
            <Layout isAdmin={true}>
              <Suspense fallback={<PageLoader />}>
                <CompaniesPage />
              </Suspense>
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route 
        path="*" 
        element={
          <Navigate 
            to={token && userType === 'admin' ? '/admin/dashboard' : '/cari-hesaplar'} 
            replace 
          />
        } 
      />
    </Routes>
    </>
  );
};

export default App;
